import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Toast, Violations } from "../../../../../../../../Components";
import { useNavigate } from "react-router-dom";
import FlightItinerary from "./itinerary";
import { useUserPolicy } from "../../../../../../../../Hooks/useUserPolicy";
import { useUserConfig } from "../../../../../../../../Hooks/useUserConfig";
import AskApproval from "./askApproval";
import SendToAdmin from "./sendToAdmin";
import { apis, post } from "../../../../../../../../Utils/axios";
import BaggageDetails from "./details";
import FareRules from "./fareRules";
import { useClientSettings } from "Hooks/useClientSettings";
import moment from "moment";
import { FaShieldAlt } from "react-icons/fa";
import { formatedPrice } from "../../../../../../../../Utils/utils";
import { getBaggageFromFare, applyBaggageToSegments } from "Utils/flightBaggage";

const BOOKING_TIMEOUT_MS = 5 * 60 * 1000;

function Summary({ approval, selectedFlight = {}, journeyKey, passengers = {}, approvalId, requestId, fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, flowId, onClose }) {
	const selectedFare = selectedFlight?.selectedFare;
	const { checkUserPermission } = useUserConfig();
	const { travelPolicy } = useUserPolicy();
	const { currency } = useClientSettings();
	const adminBooking = travelPolicy?.domesticFlight?.adminBooking && !checkUserPermission("TA");
	const approvedAmount = approval?.amount;
	const askApproval = !selectedFare?.inPolicy && !checkUserPermission("TA") && (approvedAmount ? selectedFare?.price > Number(approvedAmount) : true);

	const segments = selectedFlight?.onwardSegments || selectedFlight?.returnSegments;
	const flightDetails = segments[0];
	const onwardSegments = selectedFlight?.onwardSegments;
	const baggageSegment = selectedFare?.baggage
		? applyBaggageToSegments([onwardSegments[0]], selectedFare.baggage)[0]
		: (() => {
				const { checkin, cabin } = getBaggageFromFare(selectedFare, onwardSegments);
				return { ...onwardSegments[0], checkinBaggage: checkin, cabinBaggage: cabin };
		  })();
	const NAVIGATE = useNavigate();
	const [Loading, setLoading] = useState();
	const [ConfirmData, setConfirmData] = useState(false);
	const [openFareRulesSignal, setOpenFareRulesSignal] = useState(0);
	const [highlightedSection, setHighlightedSection] = useState("");
	const highlightTimerRef = useRef(null);
	const itineraryRef = useRef(null);
	const detailsRef = useRef(null);
	const policyRef = useRef(null);

	useEffect(() => {
		return () => {
			if (highlightTimerRef.current) {
				clearTimeout(highlightTimerRef.current);
			}
		};
	}, []);

	const jumpTo = (section, ref) => {
		ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		setHighlightedSection(section);
		if (highlightTimerRef.current) {
			clearTimeout(highlightTimerRef.current);
		}
		highlightTimerRef.current = setTimeout(() => {
			setHighlightedSection("");
		}, 3000);
	};

	const confirmFare = () => {
		setLoading(true);
		post(
			apis.confirmRoundFlightFare,
			{
				from: fromCode,
				to: toCode,
				date: moment(date).format("DD-MMM-YYYY"),
				returnDate: moment(returnDate).format("DD-MMM-YYYY"),
				adult: String(adult),
				child: String(child),
				infant: String(infant),
				cabinClass,
				requestId,
				approvalId,
				flightOptionKey: selectedFare?.fareKey,
				flightSearchKey: selectedFlight?.sid,
				// Family fare bundle IDs are only available for supported round-trip providers
				...(selectedFare?.isFamilyFare && {
					bundledServiceId: selectedFare?.bundledServiceId,
					returnBundledServiceId: selectedFare?.returnBundledServiceId,
				}),
			},
			(r, e) => {
				setLoading(false);
				if (r?.statusMessage === "success") {
					if (r.priceDiffernce > 0.1 || r.priceDiffernce < -0.1) {
						setConfirmData(r);
						return;
					} else {
						moveNext(r);
					}
				} else if (e) {
					console.error(e);
					Toast.error("Unable to book this flight. Please choose another flight");
				}
			},
			{
				timeout: BOOKING_TIMEOUT_MS,
				headers: flowId
					? {
							"X-Flow-Id": flowId,
					  }
					: undefined,
			}
		);
	};

	const moveNext = (data) => {
		NAVIGATE(`/app/travel/flights/book`, {
			state: {
				fareConfirmDetails: data,
				flight: {
					...data.flight,
					//parse round flight data to oneway like flight data
					segments: data.flight?.onwardSegments,
					returnSegments: data.flight?.returnSegments,
					duration: data.flight?.onwardDuration,
					returnDuration: data.flight?.returnDuration,
				},
				flightOptionKey: data.selectedFlightOptionKey,
				flightSearchKey: selectedFlight?.sid,
				passengers,
				approvalId,
				requestId,
				flowId,
			},
		});
	};

	if (ConfirmData) {
		if (ConfirmData.inPolicy === false && !checkUserPermission("TA")) {
			return (
				<Modal open={ConfirmData} onClose={() => setConfirmData(false)}>
					<div className="p-6 border rounded-md">
						<p className="flex justify-start gap-4 text-5xl">
							<FaShieldAlt className="text-red-600" />
							<h1>New fare is higher than your policy limit</h1>
						</p>
						<div className="my-6">
							The fare for selected flight has been {ConfirmData.priceDiffernce > 0 ? "increased" : "decreased"} by <b>{formatedPrice(Math.abs(ConfirmData.priceDiffernce).toFixed(2))}</b>. The new fare does not fall within your policy and will require approval. You can either ask for approval or
							select another flight.
						</div>
						<Violations violations={ConfirmData?.violations} />
						<div className="pt-6 space-y-2">
							<AskApproval {...{ flight: ConfirmData.flight, fare: ConfirmData.flight?.selectedFare, type: "roundtrip", violations: ConfirmData?.violations }} />
							<Button onClick={() => setConfirmData(false)} variant="outlined" className="w-full">
								Choose another flight
							</Button>
						</div>
					</div>
				</Modal>
			);
		}

		return (
			<Modal open={ConfirmData} onClose={() => setConfirmData(false)}>
				<div className="p-6 text-center border rounded-md">
					<img src={`https://cdn.travelwings.com/assets/corp/images/emojis/${ConfirmData.priceDiffernce > 0 ? "thinking" : "smile"}.png`} alt="Eyes Emoji" className="w-20 m-auto mb-6" />
					<h1>Fare changed by Airline</h1>
					<div className="mt-2">
						The fare for your booking has been {ConfirmData.priceDiffernce > 0 ? "increased" : "decreased"} by {formatedPrice(Math.abs(ConfirmData.priceDiffernce).toFixed(2))}. You can either continue with the new price of {formatedPrice(ConfirmData.currentPrice.toFixed(2))} or try searching again.
					</div>
					<div className="pt-6 space-y-2">
						<Button onClick={() => moveNext(ConfirmData)} variant="primary" className="w-full">
							Continue with new price
						</Button>
						<Button onClick={() => setConfirmData(false)} variant="outlined" className="w-full">
							Choose another flight
						</Button>
					</div>
				</div>
			</Modal>
		);
	}

	return (
		<div className="flex flex-col w-full h-full overflow-hidden fade-in-right">
			<div className="flex-shrink-0 p-4 pb-3 border-b border-surface bg-canvas">
				<div className="flex items-start justify-between gap-3">
					<div className="align-center min-w-0">
						<img src={flightDetails.logo} alt="airline-logo" className="w-10 h-10 mr-4 rounded-full" />
						<h3 className="truncate">{flightDetails.airlineName}</h3>
					</div>
					{onClose ? (
						<Button variant="outlined" className="btn-xs shrink-0" onClick={onClose}>
							Close
						</Button>
					) : null}
				</div>
			</div>
			<div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6 p-4">
				<div
					ref={itineraryRef}
					className={`transition-all duration-300 rounded-md ${
						highlightedSection === "itinerary" ? "ring-1 ring-primary/40 bg-primary/5 p-2 -m-2" : ""
					}`}
				>
					<FlightItinerary onwardSegments={selectedFlight?.onwardSegments} returnSegments={selectedFlight?.returnSegments} />
				</div>
				{selectedFlight?.onwardSegments && (
					<>
						<div
							ref={detailsRef}
							className={`transition-all duration-300 rounded-md ${
								highlightedSection === "baggage" ? "ring-1 ring-primary/40 bg-primary/5 p-2 -m-2" : ""
							}`}
						>
							<BaggageDetails segment={baggageSegment} passengers={passengers} />
						</div>
						<div
							ref={policyRef}
							className={`transition-all duration-300 rounded-md ${
								highlightedSection === "policy" ? "ring-1 ring-primary/40 bg-primary/5 p-2 -m-2" : ""
							}`}
						>
							<FareRules {...{ refundable: selectedFare?.refundable, fromCode, toCode, date, adult, child, infant, cabinClass, requestId, approvalId, flightOptionKey: selectedFare?.fareKey, flightSearchKey: selectedFlight?.sid, openSignal: openFareRulesSignal, showViewRulesButton: false }} />
						</div>
					</>
				)}
			</div>
			<div className="flex-shrink-0 p-3 border-t border-surface bg-canvas">
				<div className="mb-3 flex flex-wrap gap-2">
					<Button variant="outlined" className="btn-xs" onClick={() => jumpTo("itinerary", itineraryRef)}>
						Itinerary
					</Button>
					<Button variant="outlined" className="btn-xs" onClick={() => jumpTo("baggage", detailsRef)}>
						Baggage
					</Button>
					<Button variant="outlined" className="btn-xs" onClick={() => jumpTo("policy", policyRef)}>
						Policy
					</Button>
				</div>
				<div className="gap-1 mb-1 text-xs text-secondary">
					For {passengers.adult} Adult
					{passengers.child > 0 && <>, {passengers.child} Child</>}
					{passengers.infant > 0 && <>, {passengers.infant} Infant</>}
				</div>
				<h1 className="text-lg font-semibold mb-3">
					Total {currency} {formatedPrice(selectedFare?.price)}
				</h1>
				<div className="mb-3 p-2 rounded-md border border-surface bg-light flex items-center justify-between gap-2">
					<div className="text-xs text-secondary">{selectedFare?.refundable ? "Refundable fare" : "Non-refundable fare"} - review fare rules before continuing</div>
					<Button variant="outlined" className="btn-xs shrink-0" onClick={() => setOpenFareRulesSignal(Date.now())}>
						Fare Rules
					</Button>
				</div>
				{askApproval ? (
					<AskApproval
						{...{
							flight: selectedFlight,
							fare: selectedFare,
							violations: selectedFlight.violations,
							type: "roundtrip",
						}}
					/>
				) : adminBooking ? (
					<SendToAdmin
						{...{
							selectedFlight,
							type: "round",
							approvalId,
						}}
					/>
				) : (
					<div className="flex gap-2">
						<Button variant="outlined" onClick={onClose} className="btn-sm w-auto">
							Cancel
						</Button>
						<Button loading={Loading} variant="primary" onClick={confirmFare} className="btn-sm flex-1">
							Continue
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

export default Summary;
